import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { IngredientPhotoDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { ReportDialog } from '../ReportDialog';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';

const photosKey = (ingredientId: string) => ['ingredients', 'photos', ingredientId] as const;

/** Shared, unattributed shelf photos for an ingredient, each with a Report action (D12). */
export function SharedPhotos({ ingredientId, name }: { ingredientId: string; name: string }) {
  const qc = useQueryClient();
  const [reporting, setReporting] = useState<IngredientPhotoDto | null>(null);
  const photos = useQuery({
    queryKey: photosKey(ingredientId),
    queryFn: () => api.media.ingredientPhotos(ingredientId),
  });

  if (photos.isPending) return <p className="text-sm text-muted">Loading…</p>;
  if (photos.isError) return <Alert tone="error">{messageForError(photos.error)}</Alert>;
  if (photos.data.photos.length === 0) {
    return <p className="text-sm text-muted">No photos yet. Add one when you check it off.</p>;
  }
  return (
    <>
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {photos.data.photos.map((p) => (
          <li key={p.id} className="flex flex-col gap-1">
            <img
              src={p.thumbUrl}
              alt={`Shelf photo of ${name}`}
              loading="lazy"
              className="aspect-square w-full rounded-lg border border-line object-cover"
            />
            <button
              type="button"
              onClick={() => setReporting(p)}
              className="self-start text-xs text-muted underline hover:text-fail"
            >
              Report
            </button>
          </li>
        ))}
      </ul>
      <ReportDialog
        open={reporting !== null}
        onClose={() => {
          setReporting(null);
          void qc.invalidateQueries({ queryKey: photosKey(ingredientId) });
        }}
        kind="ingredient_photo"
        title="Report this photo"
        description="Tell us what is wrong with this shared photo."
        send={(input) => api.media.reportPhoto(reporting!.id, input)}
      />
    </>
  );
}
